import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DbNewsItem } from "../../../lib/supabase";
import { fetchNewsItems, getLang } from "../../../lib/newsUtils";

const CATEGORY_COLORS: Record<string, string> = {
  "grant-call": "bg-gold/10 text-gold-dark",
  news: "bg-navy/10 text-navy",
  event: "bg-emerald-50 text-emerald-700",
  result: "bg-cool-gray-100 text-navy/60",
};

const CATEGORY_KEYS = [
  { value: "all", i18nKey: "news.cat.all" },
  { value: "grant-call", i18nKey: "news.cat.grant-call" },
  { value: "news", i18nKey: "news.cat.news" },
  { value: "event", i18nKey: "news.cat.event" },
  { value: "result", i18nKey: "news.cat.result" },
];

function NewsCard({ item, featured = false }: { item: DbNewsItem; featured?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { t, i18n } = useTranslation();

  const formatDate = (d: string) => {
    const locale = i18n.language.startsWith("ro")
      ? "ro-RO"
      : i18n.language.startsWith("ru")
      ? "ru-RU"
      : "en-GB";
    return new Date(d).toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  if (featured) {
    return (
      <div
        ref={ref}
        className={`group grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden rounded-lg border border-cool-gray-200 transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <Link to={`/news/${item.slug}`} className="relative h-64 lg:h-auto overflow-hidden block cursor-pointer">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        <div className="bg-white p-8 md:p-10 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${CATEGORY_COLORS[item.category]}`}>
              {item.category_label}
            </span>
            {item.deadline && (
              <span className="text-xs text-red-600 font-medium flex items-center gap-1">
                <i className="ri-time-line" />
                {t("news.deadline")} {formatDate(item.deadline)}
              </span>
            )}
          </div>
          <Link to={`/news/${item.slug}`} className="cursor-pointer">
            <h2 className="text-xl md:text-2xl font-bold text-navy leading-snug mb-3 hover:text-gold transition-colors">{item.title}</h2>
          </Link>
          <p className="text-sm text-navy/60 leading-relaxed mb-6">{item.excerpt}</p>
          <div className="flex items-center justify-between mt-auto flex-wrap gap-3">
            <span className="text-xs text-navy/40">{formatDate(item.date)}{item.source ? ` · ${item.source}` : ""}</span>
            <Link
              to={`/news/${item.slug}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy hover:text-gold transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("news.readMore")}
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`group bg-white rounded-lg border border-cool-gray-200 overflow-hidden hover:border-cool-gray-300 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      <Link to={`/news/${item.slug}`} className="block cursor-pointer">
        <div className="relative h-48 overflow-hidden">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-3 left-3">
            <span className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${CATEGORY_COLORS[item.category]}`}>
              {item.category_label}
            </span>
          </div>
        </div>
      </Link>
      <div className="p-6">
        {item.deadline && (
          <p className="text-xs text-red-600 font-medium flex items-center gap-1 mb-2">
            <i className="ri-time-line" />
            {t("news.deadline")} {formatDate(item.deadline)}
          </p>
        )}
        <Link to={`/news/${item.slug}`} className="cursor-pointer">
          <h3 className="text-base font-semibold text-navy leading-snug mb-3 line-clamp-3 hover:text-gold transition-colors">{item.title}</h3>
        </Link>
        <p className="text-sm text-navy/55 leading-relaxed mb-5 line-clamp-3">{item.excerpt}</p>
        <div className="flex items-center justify-between pt-4 border-t border-cool-gray-100 flex-wrap gap-2">
          <span className="text-xs text-navy/35">{formatDate(item.date)}</span>
          <Link
            to={`/news/${item.slug}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy hover:text-gold transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("news.readMore")}
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function NewsList() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [allNews, setAllNews] = useState<DbNewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const lang = getLang(i18n.language);
      const items = await fetchNewsItems({ lang, publishedOnly: true });
      setAllNews(items);
      setLoading(false);
    };
    load();
  }, [i18n.language]);

  const filtered = allNews.filter((item) => {
    const matchCat = activeCategory === "all" || item.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      item.title.toLowerCase().includes(q) ||
      item.excerpt.toLowerCase().includes(q) ||
      (item.tags ?? []).some((tag) => tag.toLowerCase().includes(q));
    return matchCat && matchSearch;
  });

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <main className="pt-16">
      {/* Page hero */}
      <section className="bg-navy py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
            {t("news.hero.label")}
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight max-w-2xl">
            {t("news.hero.title")}
          </h1>
          <p className="text-white/55 text-base mt-4 max-w-xl leading-relaxed">
            {t("news.hero.p")}
          </p>
        </div>
      </section>

      {/* Filters & search */}
      <section className="bg-white border-b border-cool-gray-200 sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4">
            <div className="flex items-center gap-1 flex-wrap">
              {CATEGORY_KEYS.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setActiveCategory(cat.value)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.value
                      ? "bg-navy text-white"
                      : "text-navy/55 hover:text-navy hover:bg-cool-gray-100"
                  }`}
                >
                  {t(cat.i18nKey)}
                </button>
              ))}
            </div>

            <div className="relative flex-shrink-0">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-navy/30">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("news.search")}
                className="pl-9 pr-4 py-2 text-sm text-navy bg-cool-gray-50 border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors w-56"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-cool-gray-50 py-14 md:py-20 min-h-[60vh]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="py-24 flex items-center justify-center">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line text-3xl text-navy/30 animate-spin" />
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 text-navy/40">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-search-line text-4xl" />
              </div>
              <p className="text-base font-medium">{t("news.noResults.title")}</p>
              <p className="text-sm mt-1">{t("news.noResults.p")}</p>
            </div>
          ) : (
            <>
              {featured && (
                <div className="mb-10">
                  <p className="text-xs uppercase tracking-widest text-gold font-medium mb-5">
                    {t("news.latest")}
                  </p>
                  <NewsCard item={featured} featured />
                </div>
              )}

              {rest.length > 0 && (
                <>
                  <hr className="border-cool-gray-200 mb-10" />
                  <p className="text-xs uppercase tracking-widest text-navy/35 font-medium mb-8">
                    {t("news.moreUpdates")}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((item) => (
                      <NewsCard key={item.id} item={item} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* Subscription CTA */}
      <section className="bg-navy py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <p className="text-xs uppercase tracking-widest text-gold font-medium mb-3">
                {t("news.sub.label")}
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-white leading-snug max-w-md">
                {t("news.sub.title")}
              </h2>
              <p className="text-white/55 text-sm mt-2">
                {t("news.sub.p")}
              </p>
            </div>
            <a
              href="/#contact"
              className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-white font-semibold text-sm rounded-md hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {t("news.sub.btn")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-send-line text-base" />
              </span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
